import { FunctionComponent, useEffect, useState } from 'react';
import { PageHeader } from '../../components/layout/pageHeader';
import { TextGrid } from '../../components/layout/textGrid';
import { Container } from '../../components/layout/container';
import { Main } from '../../components/layout/main';
import { Loader } from '../../components/common/loader';
import { ContactsForm } from '../contacts/contactsForm';
import styles from './software-development.module.css';

export const SoftwareDevelopment: FunctionComponent = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add('software-development');
    return () => {
      document.body.classList.remove('software-development');
    };
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Main>
        <Loader />
      </Main>
    );
  }

  return (
    <Main>
      <PageHeader
        subtitle="Software development"
        title="Products and Services"
      />
      <Container>
        <div className={styles.section}>
          <p className={styles.paragraph}>
            At BlockNote SLLC, we're a startup driven by a passion for bringing fresh perspectives to the blockchain space. Our goal is to provide businesses with secure, scalable, and intuitive tools that unlock the potential of blockchain technology in practical, meaningful ways.
          </p>
          
          <h2 className={styles.title}>Who We Are</h2>
          <p className={styles.paragraph}>
            At BlockNote, we're committed to building efficient and reliable systems that make blockchain technology accessible and impactful. Our focus is on helping businesses securely store, exchange, and manage data while exploring the exciting possibilities of decentralized solutions.
          </p>
          
          <div className={styles.quote}>
            <p>"Our research and development is focused on creating practical tools that solve real problems. Building platforms for crypto exchange is one of our main priorities, and the Beta project is our way of showing what we're capable of."</p>
            <div className={styles.quoteAuthor}>— Sandis Liekniņš, CEO</div>
          </div>
          
          <h2 className={styles.title}>BlockNote Exchange: Trade with Ease</h2>
          <h3 className={styles.subtitle}>A Beta Platform That Works for You</h3>
          <p className={styles.paragraph}>
            BlockNote Exchange is our Beta platform—a straightforward, user-focused cryptocurrency exchange designed to make trading easier while demonstrating the reliability and flexibility of BlockNote's infrastructure.
          </p>
          
          <div className={styles.features}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 5L5 12.5L20 20L35 12.5L20 5Z" fill="#30d475"/>
                  <path d="M5 27.5L20 35L35 27.5L20 20L5 27.5Z" fill="#30d475"/>
                  <path d="M5 20L20 27.5L35 20L20 12.5L5 20Z" fill="#30d475" fillOpacity="0.3"/>
                </svg>
              </div>
              <h4>Essential Tools</h4>
              <p>Access a solid set of tools to support your trading needs.</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="10" width="30" height="20" rx="2" fill="#30d475" fillOpacity="0.2"/>
                  <path d="M10 15H30" stroke="#30d475" strokeWidth="2"/>
                  <path d="M10 20H30" stroke="#30d475" strokeWidth="2"/>
                  <path d="M10 25H20" stroke="#30d475" strokeWidth="2"/>
                </svg>
              </div>
              <h4>Intuitive Interface</h4>
              <p>Navigate with confidence using a clean and accessible design.</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="15" fill="#30d475" fillOpacity="0.2"/>
                  <path d="M15 20L18 23L25 16" stroke="#30d475" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4>Dependable Performance</h4>
              <p>Built with security and functionality in mind.</p>
            </div>
          </div>
          
          <div className={styles.quote}>
            <p>"If you take a quick look at BlockNote Exchange, you'll see how we've made trading simpler and advanced blockchain technology more accessible to everyone,"</p>
            <div className={styles.quoteAuthor}>— Maksims Rižikovs, CFO.</div>
          </div>
          
          <p className={styles.paragraph}>
            While BlockNote Exchange provides must-have trading functionalities, its primary purpose is to showcase its unique system flexibility, and with that comes the reliability of BlockNote's proprietary technology.
          </p>
          
          <p className={styles.paragraph}>
            The technology is highly customizable, allowing us to adapt our software solutions to specific clients' needs. And that's an advantage.
          </p>
        </div>
      </Container>

      <div className={styles.successStories}>
        <Container>
          <h2 className={styles.title}>Our First Success Stories</h2>
          <p className={styles.paragraph}>
            At BlockNote, we're setting a new standard for quality in blockchain technology. We're tackling industry challenges head-on, all while always keeping the user at the center of everything we do. Every step we take is progress, and we're proud of what we're achieving.
          </p>
          
          <ul className={styles.successList}>
            <li className={styles.successItem}>
              <div className={styles.successIcon}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10L9 12L13 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4>Secured our first major client</h4>
                <p>Marking a key milestone that proves the market potential of our platform.</p>
              </div>
            </li>
            <li className={styles.successItem}>
              <div className={styles.successIcon}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 10L9 12L13 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4>Enhanced platform functionality</h4>
                <p>By listening closely to real-world user feedback, ensuring our platform is adaptable and ready for future growth.</p>
              </div>
            </li>
          </ul>
          <div className={styles.quote}>
            <p>"We're just getting started, but with new partners and our growing expertise, we're making blockchain more accessible, secure, and ready for the future."</p>
            <div className={styles.quoteAuthor}>— Sandis Liekniņš, CEO.</div>
          </div>
        </Container>
      </div>

      <Container>
        <div className={styles.section}>
          <h2 className={styles.title}>What's Next for BlockNote?</h2>
          <p className={styles.paragraph}>
            We're not just building platforms; we're focused on creating something that truly matters in the blockchain space. Our next steps include:
          </p>
          
          <ul className={styles.futureSteps}>
            <li className={styles.futureStep}>
              <div className={styles.stepIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#30d475"/>
                  <path d="M2 17L12 22L22 17L12 12L2 17Z" fill="#30d475" fillOpacity="0.3"/>
                </svg>
              </div>
              <div className={styles.stepContent}>
                <h4>Advanced Features</h4>
                <p>Using real user feedback to make our platform smarter and more useful.</p>
              </div>
            </li>
            <li className={styles.futureStep}>
              <div className={styles.stepIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#30d475" fillOpacity="0.3"/>
                  <path d="M12 2V12L17 7" stroke="#30d475" strokeWidth="2"/>
                </svg>
              </div>
              <div className={styles.stepContent}>
                <h4>Market Expansion</h4>
                <p>Expanding into new regions so we can reach more people and help them use blockchain in meaningful ways.</p>
              </div>
            </li>
            <li className={styles.futureStep}>
              <div className={styles.stepIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 7L7 17" stroke="#30d475" strokeWidth="2"/>
                  <circle cx="7" cy="7" r="5" fill="#30d475" fillOpacity="0.3"/>
                  <circle cx="17" cy="17" r="5" fill="#30d475" fillOpacity="0.3"/>
                </svg>
              </div>
              <div className={styles.stepContent}>
                <h4>Strategic Partnerships</h4>
                <p>Teaming up with industry leaders to build a stronger, more connected blockchain community.</p>
              </div>
            </li>
          </ul>
        </div>
      </Container>

      <div className={styles.contactSection}>
        <Container>
          <h2 className={styles.title}>Contact Us</h2>
          <p className={styles.paragraph}>
            We'd love to hear from you. Whether you want a demo, have questions, or are interested in partnering up, we're here to help. Get in touch with us today.
          </p>
          <ContactsForm 
            pageType="software-development" 
            className="software-development-form"
          />
        </Container>
      </div>
    </Main>
  );
};